import CircleIcon from '@mui/icons-material/Circle';
import { TimelineItem } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  updateClientContactStatus,
  LogAction,
  LogContextType,
  LogEntityType,
  saveActivityLog,
  ClientContactDTO,
  getClientContactById,
  getActivityLogs,
  getActivityLogsByEntity,
  ActivityLogDTO,
  assignDncDetails,
  DncDetailsDTO,
} from 'wisestep-api';
import {
  getFormatDate,
  WsColors,
  WsTypoColors,
  WsTypoVariants,
  ScrollableButtonTabs,
  WsTypography,
  WsTimeLine,
  formatDateInDayMonthYearTime,
  WsDrawer,
  WsPageHeader,
} from 'wisestep-base';

import { AccessControl } from '../../../../components';
import { NotesParent } from '../../../../components/communicationBar/Notes';
import { IBreadCrumbsList, WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { WsTabs } from '../../../../components/WsTabs';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext';
import {
  WsActivityProps,
  activityLogDTOHelper,
} from '../../../../features/activityLog/ActivityHelper';
import ComposableDashboardFiltersContextProvider from '../../../../features/analyticsPage/context/ComposableDashboardFiltersContext';
import { getFile } from '../../../../features/careerPageSettings/util';
import ClientContacts from '../../../../features/clientListingPage/components/ClientContacts';
import ClientOwnerPopUp from '../../../../features/clientListingPage/components/ClientOwnerPopUp';
import { buildLeadUrlFromId } from '../../../../features/clientListingPage/helper/ClientListingHelper';
import { useActiveLeadStatus } from '../../../../features/clientsSettings/hooks/useActiveLeadStatus';
import DataPrivacy from '../../../../features/conversationWidget/components/DataPrivacy';
import LeadList from '../../../../features/leads/components/LeadList';
import { buildLeadFilter } from '../../../../features/leads/helper/leadHelper';
import UserProfileOverview from '../../../../features/recruiterProfileDetails/components/userProfile/UserProfileOverview';
import { useClientAuthz, ClientAction } from '../../../../hooks/useClientAuthz';
import { useDialog } from '../../../../hooks/useDialog';
import { useDrawer } from '../../../../hooks/useDrawer';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../utils/openUrlInNewTab';
import { defaultQuery } from '../../../jobs/listing/JObSearchFilterDefaultQuery';
import {
  ClientDetailsParams,
  clientContactLayoutTabNames,
  clientsAmplitudeEvents,
  getClientOrContactListBreadCrumbs,
  limitedPrivacyOptions,
} from '../../helper/clientDetailsHelper';
import openNotesFormDialog from '../../helper/openNotesFormDialog';
import ClientSharingTable from '../clientSharing';
import DocumentsTab from '../documentsTab/DocumentsTab';
import ComposeNote from '../notes/ComposeNote';
import { notesAmplitudeEvents } from '../notes/helper/notesHelper';

import ContactProfileDetails from './ContactProfileDetails';

const ClientContactDetails = () => {
  const params = useParams<ClientDetailsParams>();
  const [param] = useSearchParams();
  const isChild: boolean = param.get('isChild') === 'true';
  const parentName: string = param.get('name') ?? '';
  const [openDrawer, closeDrawer] = useDrawer();
  const [clientContactInfo, setClientContactInfo] = useState<ClientContactDTO>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const isAuthorizedViewAnalytics = useClientAuthz(ClientAction.VIEW_ANALYTICS);
  const isAuthorizedEditContact = useClientAuthz(ClientAction.EDIT_VIEW_USER_CONTACT_DETAILS);
  const isAuthorizedAddContact = useClientAuthz(ClientAction.ADD_ASSIGN_CLIENT_CONTACT);
  const [parentTabIndex, setParentTabIndex] = useState<number>(0);
  const [getActivityData, setgetActivityData] = useState<ActivityLogDTO[]>([]);
  const [openDialog, closeDialog] = useDialog();
  const workspace = useWorkspaceContext();
  const isInDnc = clientContactInfo?.dncDetails?.isInDnc ?? false;
  const [openDNC, setOpenDNC] = useState<boolean>(false);
  const [openClientOwnerFormDialog, setOpenClientOwnerFormDialog] = useState<boolean>(false);
  const activeStatus = useActiveLeadStatus();
  const [contactImageUrl, setContactImageUrl] = useState<string | null>(null);
  const [moreOptionList, setMoreoptionList] = useState<string[]>([]);

  const handleClientContactStatus = async (id: string, markStatus: boolean, firstName: string) => {
    clientsAmplitudeEvents(
      'contact_details_status_update',
      'Contact Details Status Update',
      workspace
    );
    await updateClientContactStatus(id, markStatus);
    const avPayload = { ...activityLogDTOHelper };
    avPayload.action = LogAction.Edit;
    avPayload.description = `Marked status as ${
      markStatus === true ? 'Active' : 'Inactive'
    } for ${firstName} Client Contact under`;
    avPayload.parentEntitiyType = LogContextType.Client;
    avPayload.entityType = LogEntityType.ClientContact;
    avPayload.entityId = id;
    avPayload.parentEntitiyId = clientContactInfo?.clientId ?? '';
    avPayload.field = `Client Contact`;
    avPayload.contextData = { clientId: clientContactInfo?.clientId ?? '' };
    await saveActivityLog(avPayload);
    getClientContact();
  };

  const onNavigateToJobs = async (email: string) => {
    clientsAmplitudeEvents('contact_details_jobs', 'Contact Details Jobs', workspace);
    openUrlInNewTab(
      `/jobs?d=${getEncodedQueryParams({
        ...defaultQuery,
        clientList: [{ name: parentName }],
        clientRecruiterList: [{ emailAddress: email ?? '' }],
      })}`
    );
  };

  const getClientContact = useCallback(async () => {
    if (params.id === undefined) {
      setNotification(failed(`Id not found in the url`));
      return;
    }
    try {
      const response = await getClientContactById(params.id as string);
      if (response?.data?.data) {
        const contactData = response.data.data;
        const updatedContactData = {
          ...contactData,
          supervisorName: contactData.supervisor
            ? `${contactData.supervisor.firstName} ${contactData.supervisor.lastName}`
            : '',
          supervisorPosition: contactData?.supervisor?.position ?? '',
          tags: contactData?.tags ?? [],
        };
        setClientContactInfo(updatedContactData);
        try {
          if (updatedContactData.imageUrl) {
            const fileUrl = await getFile(updatedContactData.imageUrl);
            if (fileUrl) setContactImageUrl(fileUrl);
          }
        } catch (imageError) {
          console.warn('Failed to fetch contact image:', imageError);
        }
      }
    } catch (error) {
      setNotification(failed(`Failed to fetch Client Contact Information`));
    }
  }, [params.id, setNotification]);

  useEffect(() => {
    getClientContact();
  }, [getClientContact]);

  const activityLists: WsActivityProps[] = [];

  useEffect(() => {
    let avtivityLog: any = [];
    const fetchDataLog = async () => {
      if (clientContactInfo?.clientId) {
        try {
          const { data: actvityResponseContext } = await getActivityLogs(
            clientContactInfo?.clientId ?? ''
          );
          const { data: actvityResponseEnitiy } = await getActivityLogsByEntity(
            clientContactInfo?.clientId ?? ''
          );
          avtivityLog = actvityResponseContext.concat(actvityResponseEnitiy);
          setgetActivityData(avtivityLog);
        } catch {
          setNotification(failed(`Failed to fetch company Information`));
        }
      }
    };
    fetchDataLog();
  }, [clientContactInfo?.clientId, setNotification]);

  getActivityData
    ?.filter(
      (data: ActivityLogDTO) =>
        data.field === 'Client Contact' && data.entityId == (params.id as string)
    )
    .map((data: ActivityLogDTO) =>
      activityLists.push({
        activity: (
          <TimelineItem>
            <Grid container>
              <Grid item xs={12}>
                <WsTypography color={WsTypoColors.TERTIARY}>
                  {` At ${formatDateInDayMonthYearTime(data?.createdOn?.toString())} by ${
                    data.createdByName
                  }`}
                </WsTypography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant={WsTypoVariants.SUB_TITLE}>
                  {`${data.field} Category -> ${data.description} client ${data.parentEntitiyName}`}
                </Typography>
              </Grid>
            </Grid>
          </TimelineItem>
        ),
        leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
      })
    );

  const fullName = clientContactInfo?.firstName
    ? clientContactInfo.lastName
      ? clientContactInfo.firstName + ' ' + clientContactInfo.lastName
      : clientContactInfo.firstName
    : clientContactInfo?.lastName;

  const breadCrumbsList: IBreadCrumbsList[] = getClientOrContactListBreadCrumbs(
    param,
    fullName,
    'CLIENT_CONTACTS'
  );
  const handleOpenDrawer = () => {
    clientsAmplitudeEvents('contact_details_assign', 'Contact Details Assign', workspace);
    openDrawer({
      children: (
        <ClientSharingTable
          clientId={clientContactInfo?.clientId ?? ''}
          moduleName="CLIENT-CONTACT"
          clientContactId={clientContactInfo?.id ?? ''}
        />
      ),
      size: 'small',
      title: 'Client Contacts Access',
    });
  };

  const handleOpenClientContactsDrawer = () => {
    clientsAmplitudeEvents('contact_details_edit', 'Contact Details Edit', workspace);
    openDrawer({
      children: (
        <ClientContacts
          clientId={clientContactInfo?.clientId ?? ''}
          showSavedData={false}
          clientContactForEdit={clientContactInfo}
          closeOnUpdate={() => closeDrawer()}
        />
      ),
    });
  };

  const handleOpenNotesDialog = () => {
    clientsAmplitudeEvents('contact_details_note_add', 'Contact Details Note Add', workspace);
    openNotesFormDialog({
      clientId: clientContactInfo?.clientId ?? '',
      contactId: clientContactInfo?.id ?? '',
      clientName: clientContactInfo?.firstName,
      contactName: clientContactInfo?.firstName,
      isClient: false,
      openDialog: openDialog,
      closeDialog: closeDialog,
    });
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = clientContactLayoutTabNames();
    tabs?.[index].tabName === 'Activity' &&
      notesAmplitudeEvents(
        isChild ? 'note_contact' : 'note_child_contact',
        isChild ? 'Note Contact' : 'Note Child Contact',
        workspace
      );
    setParentTabIndex(index);
    clientsAmplitudeEvents(
      `contact_details_${tabs?.[index].tabName?.toLowerCase()}`,
      `Contact Details ${tabs?.[index].tabName}`,
      workspace
    );
  };

  const handleDncSubmit = async (contactId: string, data: DncDetailsDTO) => {
    try {
      await assignDncDetails(contactId, data);
    } catch (error) {
      console.error('Error updating DNC details / lead status:', error);
    }
  };

  useEffect(() => {
    const list: string[] = [
      'Create Note',
      ...(isAuthorizedAddContact && !isInDnc ? ['Assign user'] : []),
      ...(!isInDnc ? [`Mark as ${clientContactInfo?.markStatus ? 'Inactive' : 'Active'}`] : []),
      'Jobs',
      ...(isAuthorizedEditContact && !isInDnc ? ['Edit'] : []),
      'Update Owner',
      !isInDnc ? 'Mark DNC' : 'Remove DNC',
    ];

    setMoreoptionList(list);
  }, [clientContactInfo?.markStatus, isAuthorizedAddContact, isAuthorizedEditContact, isInDnc]);

  const onMoreOptionClick = (option: string) => {
    option === 'Create Note' && handleOpenNotesDialog();
    option === `Mark as ${clientContactInfo?.markStatus ? 'Inactive' : 'Active'}` &&
      handleClientContactStatus(
        clientContactInfo?.id ?? '',
        clientContactInfo?.markStatus == true ? false : true,
        clientContactInfo?.firstName ?? ''
      );
    option === 'Update Owner' && setOpenClientOwnerFormDialog(true);
    option === 'Assign user' && handleOpenDrawer();
    option === 'Jobs' && onNavigateToJobs(clientContactInfo?.emailAddress ?? '');
    option === 'Edit' && handleOpenClientContactsDrawer();
    option === (!isInDnc ? 'Mark DNC' : 'Remove DNC') && setOpenDNC(!openDNC);
  };

  return (
    <>
      <WsBreadCrumbs breadCrumbsList={breadCrumbsList ?? []} maxItems={4} />
      <WsPageHeader
        title={fullName}
        subtitle={`Created on ${getFormatDate(clientContactInfo?.createdOn?.toString() ?? '')}`}
        moreOptions={moreOptionList}
        onMoreOptionClick={(option) => onMoreOptionClick(option)}
        badgeLable={clientContactInfo?.markStatus ? 'Active' : 'Inactive'}
        hyperLink={{
          buttonName: 'View Lead(s)',
          path: buildLeadUrlFromId(clientContactInfo?.id ?? '', activeStatus, 'contact') ?? '',
        }}
        socialUrl={clientContactInfo?.linkedinProfile}
        profileImg={{ profileImage: contactImageUrl ?? '', required: true }}
        phoneNumbers={[clientContactInfo?.phoneNumber ?? '']}
        emails={[clientContactInfo?.emailAddress ?? '']}
      />
      {clientContactInfo && (
        <Box borderRadius={2}>
          {openDNC && clientContactInfo && (
            <WsDrawer open={openDNC} onClose={() => setOpenDNC(!openDNC)}>
              <DataPrivacy
                email={clientContactInfo?.emailAddress ?? ''}
                candidateName={`${clientContactInfo.firstName}`}
                isInDnc={isInDnc ?? false}
                dncDetails={clientContactInfo?.dncDetails}
                privacyOptions={limitedPrivacyOptions}
                restrictLabel="DNC Up to*"
                entityId={(clientContactInfo?.id as string) ?? ''}
                parentEntitiyId={(clientContactInfo?.clientId as string) ?? ''}
                entityName={clientContactInfo?.firstName}
                logContextType={LogContextType.Client}
                onSubmitApi={(data) => handleDncSubmit(clientContactInfo?.id as string, data)}
                enableActivityLog={true}
              />
            </WsDrawer>
          )}
          <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
            <ScrollableButtonTabs
              scrollList={clientContactLayoutTabNames()}
              getTabIndex={handleTabIndexChange}
              defaultValue={parentTabIndex}
            />
          </Grid>
          {parentTabIndex == 0 &&
            (isAuthorizedViewAnalytics ? (
              <ComposableDashboardFiltersContextProvider>
                <UserProfileOverview
                  clientSpocEmail={clientContactInfo.emailAddress}
                  clientContactIds={[clientContactInfo.id ?? '']}
                />
              </ComposableDashboardFiltersContextProvider>
            ) : (
              <AccessControl errorText="You don't have access to view the client contact data" />
            ))}

          {parentTabIndex == 1 &&
            (isAuthorizedViewAnalytics ? (
              <ContactProfileDetails clientContactInfo={clientContactInfo} />
            ) : (
              <AccessControl errorText="You don't have access to view the client contact data" />
            ))}

          {parentTabIndex == 2 && (
            <WsTabs
              onChange={(index) =>
                notesAmplitudeEvents(
                  index == 0 ? 'contact_details_notes' : 'contact_details_activities',
                  'Contact Details',
                  workspace
                )
              }
            >
              <WsTabs.Item label="Notes">
                <ComposeNote
                  parent={NotesParent.CLIENTCONTACT}
                  parentId={clientContactInfo?.clientId}
                  isClient={false}
                  clientContactId={clientContactInfo?.id ?? ''}
                />
              </WsTabs.Item>

              <WsTabs.Item label="Activities">
                {getActivityData && (
                  <Grid item xs={12} borderRadius={2} sx={{ background: WsColors.White }}>
                    <Box ml="-97%">
                      <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                    </Box>
                  </Grid>
                )}
              </WsTabs.Item>
            </WsTabs>
          )}

          {parentTabIndex == 3 && (
            <DocumentsTab
              clientId={clientContactInfo?.clientId ?? ''}
              clientContactId={clientContactInfo.id ?? ''}
              clientName={parentName ?? ''}
              entityType="contact"
            />
          )}
          {parentTabIndex == 4 && (
            <LeadList
              initialFilter={buildLeadFilter(
                'contact',
                clientContactInfo.clientId,
                clientContactInfo.id ?? ''
              )}
              fromClientTab
            />
          )}
        </Box>
      )}
      {openClientOwnerFormDialog && clientContactInfo?.id && (
        <ClientOwnerPopUp
          id={clientContactInfo?.id ?? ''}
          entityType="contact"
          clientOwner={clientContactInfo.owner || null}
          open={openClientOwnerFormDialog}
          onCancel={() => {
            setOpenClientOwnerFormDialog(false);
          }}
          onClientContactSave={(updatedContact: ClientContactDTO) => {
            setClientContactInfo(updatedContact);
            setOpenClientOwnerFormDialog(false);
          }}
        />
      )}
    </>
  );
};

export default ClientContactDetails;
